import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "@/lib/utils";
import { type AsChildProps, resolveRenderProp } from "./render-prop";

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 text-sm font-medium whitespace-nowrap transition-[color,background-color,border-color,opacity,box-shadow,transform] duration-150 ease-out-strong outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-']):not([class^='h-']):not([class*='_h-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "border border-black/10 bg-black/5 text-text-primary shadow-[inset_0_1px_1px_rgba(255,255,255,0.2),inset_0_-2px_10px_rgba(0,0,0,0.05)] backdrop-blur-md hover:bg-black/10 active:scale-[0.98] dark:border-white/12 dark:bg-white/[0.08] dark:text-white dark:shadow-[inset_0_1px_1px_rgba(255,255,255,0.15),inset_0_-2px_10px_rgba(0,0,0,0.2),0_10px_20px_-5px_rgba(0,0,0,0.4)] dark:hover:bg-white/[0.14] pointer-fine:hover:scale-[1.02]",
        solid:
          "bg-brand text-brand-foreground hover:bg-brand-hover focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.97] dark:bg-white dark:text-black dark:hover:bg-white/92",
        destructive:
          "border border-destructive/20 bg-destructive/15 text-destructive shadow-xs hover:bg-destructive/25 focus-visible:ring-destructive/20 active:scale-[0.98] dark:border-destructive/30 dark:bg-destructive/10 dark:text-red-400 dark:hover:bg-destructive/20 pointer-fine:hover:scale-[1.02]",
        outline:
          "border border-black/15 bg-transparent text-text-primary hover:bg-black/5 active:scale-[0.99] dark:border-white/10 dark:text-white/90 dark:hover:bg-white/5 pointer-fine:hover:scale-[1.01]",
        secondary:
          "border border-black/5 bg-black/5 text-text-secondary hover:bg-black/8 active:scale-[0.99] dark:border-white/6 dark:bg-white/[0.03] dark:text-white/80 dark:hover:bg-white/[0.08] pointer-fine:hover:scale-[1.01]",
        ghost:
          "text-text-primary hover:bg-black/5 active:scale-[0.99] dark:text-white/80 dark:hover:bg-white/5 pointer-fine:hover:scale-[1.01]",
        link: "text-brand underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 rounded-full px-4.5 has-[>svg]:px-3.5",
        sm: "h-8 gap-1.5 rounded-full px-3.5 text-xs has-[>svg]:px-3",
        lg: "h-10 rounded-full px-6 text-base has-[>svg]:px-5",
        icon: "size-9 rounded-full",
        "icon-sm": "size-8 rounded-full",
        "icon-xs": "size-7 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  asChild = false,
  children,
  className,
  nativeButton,
  render,
  size,
  variant,
  ...props
}: React.ComponentProps<typeof ButtonPrimitive> &
  VariantProps<typeof buttonVariants> &
  AsChildProps) {
  const renderProps = resolveRenderProp({
    asChild,
    children,
    nativeButton,
    render,
  });

  return (
    <ButtonPrimitive
      className={cn(buttonVariants({ variant, size, className }))}
      data-slot="button"
      nativeButton={renderProps.nativeButton}
      render={renderProps.render}
      {...props}
    >
      {renderProps.children}
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
